<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_customers')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <b-button
            variant="primary"
            class="create-btn"
            @click="csvExportModal.show = true"
          >
            {{ $t('globals.export').toUpperCase() }}
          </b-button>
        </div>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.name") }}</label>
              <b-form-input
                v-model="searchQuery.name"
                @keyup.enter="searchCustomersLikeName"
                class="search-input"
                :placeholder="$t('globals.name') + '...'"
                :disabled="getAllCustomersLoading"
              />
            </div>

            <div class="filter-group">
              <div class="filter-item">
                <label class="filter-label">WebApp ID</label>
                <b-form-input
                  v-model="searchQuery.webAppId"
                  @keyup.enter="searchCustomersWebAppId"
                  class="filter-select"
                  placeholder="WebApp ID..."
                  :disabled="getAllCustomersLoading"
                />
              </div>

              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.debiteur_no") }}</label>
                <b-form-input
                  v-model="searchQuery.debiteurNo"
                  @keyup.enter="searchCustomersDebiteurNo"
                  class="filter-select"
                  :placeholder="$t('globals.debiteur_no') + '...'"
                  :disabled="getAllCustomersLoading"
                />
              </div>
            </div>

            <b-button
              variant="primary"
              class="search-btn"
              @click="selectSearch"
            >
              {{ $t("globals.search").toUpperCase() }}
            </b-button>
          </div>

          <!-- Table -->
          <div class="table-wrapper" style="position: relative; min-height: 300px;">
            <!-- Loading Overlay -->
            <div v-if="getAllCustomersLoading" class="loading-overlay">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>

            <!-- Table Content -->
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Empty State Row -->
                <tr v-if="!getAllCustomersLoading && getAllCustomers.length === 0">
                  <td :colspan="fields.length" class="empty-state-cell">
                    {{ $t("globals.no_results") }}
                  </td>
                </tr>

                <!-- Data Rows -->
                <tr
                  v-for="item in getAllCustomers"
                  :key="item.id"
                  class="table-row"
                  @click="rowClicked(item)"
                >
                  <td>{{ item.id }}</td>
                  <td>
                    <span class="customer-link" style="cursor: pointer;">
                      {{ item.company_name }}
                    </span>
                  </td>
                  <td>{{ item.no_devices || 0 }}</td>
                  <td>{{ item.web_app_id || '-' }}</td>
                  <td>{{ item.debiteur_no || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination-section" v-if="paginationEnabled">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + getAllCustomers.length) }}</span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="offset === 0 || getAllCustomersLoading"
                @click="fetchLess"
              >
                &lt;
              </button>
              <button
                class="page-btn"
                :disabled="getAllCustomers.length < limit || getAllCustomersLoading"
                @click="fetchMore"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <export-csv-modal :show="csvExportModal.show" :close="closeModals"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    "export-csv-modal": () =>
      import("@/components/customers/all_customers/ExportCsvModal.vue"),
  },
  data() {
    return {
      csvExportModal: {
        show: false
      },
      searchQuery: {
        name: null,
        webAppId: null,
        debiteurNo: null
      },
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true
        },
        {
          key: "company_name",
          label: this.$t("globals.name"),
          sortable: true
        },
        {
          key: "no_devices",
          label: this.$t("globals.number_of_devices"),
          sortable: false
        },
        {
          key: "web_app_id",
          label: "WebApp ID",
          sortable: true
        },
        {
          key: "debiteur_no",
          label: this.$t("globals.debiteur_no"),
          sortable: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters("customers", ["getAllCustomers", "getAllCustomersLoading"]),
    paginationEnabled() {
      return (
        (this.searchQuery.name == null || this.searchQuery.name == '')
        && (this.searchQuery.webAppId == null || this.searchQuery.webAppId == '')
        && (this.searchQuery.debiteurNo == null || this.searchQuery.debiteurNo == '')
      )
    }
  },
  methods: {
    ...mapActions("customers", [
      "fetchCustomersPagination",
      "fetchCustomersLikeName",
      "fetchCustomersDebiteurNo",
      "fetchCustomersWebAppId"
    ]),
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "customer", params: { id: item.id } });
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
      this.fetch();
    },
    fetchMore() {
      this.offset += 20;
      this.fetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.fetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.name = null;
      this.searchQuery.webAppId = null;
      this.searchQuery.debiteurNo = null;
    },
    fetch() {
      let order_by = this.sortBy;
      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchCustomersPagination({
        this_ref: this,
        limit: this.limit,
        offset: this.offset,
        order: order_by
      });
    },
    selectSearch() {
      if (
        this.searchQuery.name != null
        && this.searchQuery.name.length > 2
      ) {
        this.searchCustomersLikeName();
      } else if (
        this.searchQuery.webAppId != null
        && this.searchQuery.webAppId.length > 0
      ) {
        this.searchCustomersWebAppId();
      } else if (
        this.searchQuery.debiteurNo != null
        && this.searchQuery.debiteurNo.length > 0
      ) {
        this.searchCustomersDebiteurNo();
      } else {
        this.fetch();
      }
    },
    searchCustomersLikeName() {
      if (this.searchQuery.name != null && this.searchQuery.name.length > 2) {
        this.searchQuery.webAppId = null;
        this.searchQuery.debiteurNo = null;

        this.resetOffsetSort();
        this.fetchCustomersLikeName({name: this.searchQuery.name, this_ref: this,});
      }
    },
    searchCustomersDebiteurNo() {
      if (this.searchQuery.debiteurNo != null) {
        this.searchQuery.webAppId = null;
        this.searchQuery.name = null;

        this.resetOffsetSort();
        this.fetchCustomersDebiteurNo({debiteurNo: this.searchQuery.debiteurNo, this_ref: this,});
      }
    },
    searchCustomersWebAppId() {
      if (this.searchQuery.webAppId != null) {
        this.searchQuery.debiteurNo = null;
        this.searchQuery.name = null;

        this.resetOffsetSort();
        this.fetchCustomersWebAppId({webAppId: this.searchQuery.webAppId, this_ref: this,});
      }
    },
    closeModals() {
      this.csvExportModal.show = false;
      this.resetReload();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    }
  },
  watch: {
    'searchQuery.name'(newVal) {
      if (newVal) {
        this.searchQuery.webAppId = null;
        this.searchQuery.debiteurNo = null;
        this.resetOffsetSort();
      }
    },
    'searchQuery.webAppId'(newVal) {
      if (newVal) {
        this.searchQuery.name = null;
        this.searchQuery.debiteurNo = null;
        this.resetOffsetSort();
      }
    },
    'searchQuery.debiteurNo'(newVal) {
      if (newVal) {
        this.searchQuery.name = null;
        this.searchQuery.webAppId = null;
        this.resetOffsetSort();
      }
    }
  },
  mounted() {
    this.resetReload();
  }
};
</script>
